import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Users, 
  Calendar, 
  FileText, 
  CreditCard, 
  Download, 
  Eye,
  Clock,
  MapPin,
  Phone,
  Mail,
  Upload,
  Receipt,
  Building,
  PartyPopper,
  Loader2,
  CheckCircle,
  AlertCircle,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const BANK_DETAILS = {
  account_name: "INNOVATIVE CARE AND REHAB CENTRE LIMITED",
  account_number: "31528058",
  sort_code: "56-00-18",
  bank_name: "NatWest"
};

export default function ParentDashboardPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [events, setEvents] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedChild) {
      fetchChildData(selectedChild.id);
    }
  }, [selectedChild]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [childrenRes, eventsRes, invoicesRes, receiptsRes, historyRes] = await Promise.all([
        api.get('/parent-portal/my-children'),
        api.get('/parent-portal/events'),
        api.get('/parent-portal/invoices'),
        api.get('/parent-portal/receipts'),
        api.get('/parent-portal/payment-history')
      ]);

      setChildren(childrenRes.data.children || []);
      setEvents(eventsRes.data.events || []);
      setInvoices(invoicesRes.data.invoices || []);
      setReceipts(receiptsRes.data.receipts || []);
      setPaymentHistory(historyRes.data.transactions || []);

      if (childrenRes.data.children?.length > 0) {
        setSelectedChild(childrenRes.data.children[0]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchChildData = async (childId) => {
    try {
      const [aptsRes, docsRes] = await Promise.all([
        api.get(`/parent-portal/child/${childId}/appointments`),
        api.get(`/parent-portal/child/${childId}/documents`)
      ]);

      setAppointments(aptsRes.data.appointments || []);
      setDocuments(docsRes.data.documents || []);
    } catch (error) {
      console.error('Failed to fetch child data:', error);
    }
  };

  const initiateStripePayment = async (invoiceId) => {
    try {
      const response = await api.post('/parent-portal/payment/stripe', {
        invoice_id: invoiceId,
        payment_method: 'stripe',
        origin_url: window.location.origin
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    }
  };

  const initiateBankTransfer = async (invoiceId) => {
    try {
      const response = await api.post('/parent-portal/payment/bank-transfer', {
        invoice_id: invoiceId
      });

      if (response.data.success) {
        toast.success(
          <div className="space-y-2">
            <p className="font-medium">Bank Transfer Reference Generated</p>
            <p className="text-sm">Reference: <strong>{response.data.reference}</strong></p>
            <p className="text-sm">Amount: £{response.data.amount.toFixed(2)}</p>
          </div>,
          { duration: 10000 }
        );
        fetchData(); // Refresh invoices
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate transfer reference');
    }
  };

  const downloadReceipt = async (receiptId) => {
    try {
      const response = await api.get(`/parent-portal/receipt/${receiptId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `receipt_${receiptId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download receipt');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: { color: 'bg-green-100 text-green-800', label: 'Paid' },
      pending: { color: 'bg-amber-100 text-amber-800', label: 'Pending' },
      draft: { color: 'bg-slate-100 text-slate-800', label: 'Draft' },
      overdue: { color: 'bg-red-100 text-red-800', label: 'Overdue' },
      completed: { color: 'bg-green-100 text-green-800', label: 'Completed' },
      pending_verification: { color: 'bg-blue-100 text-blue-800', label: 'Pending Verification' }
    };
    const config = statusConfig[status] || { color: 'bg-slate-100 text-slate-800', label: status };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parent-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Parent Portal</h1>
          <p className="text-slate-600">Welcome back, {user?.first_name}!</p>
        </div>
      </div>

      {/* Children Selector */}
      {children.length > 1 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-[#2a9d8f]" />
              Your Children
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {children.map(child => (
                <Button
                  key={child.id}
                  variant={selectedChild?.id === child.id ? "default" : "outline"}
                  className={selectedChild?.id === child.id ? "bg-[#1e3a5f]" : ""}
                  onClick={() => setSelectedChild(child)}
                >
                  {child.first_name} {child.last_name}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="appointments">Appointments</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="payments">Payments</TabsTrigger>
          <TabsTrigger value="events">Events</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600">Children</p>
                    <p className="text-2xl font-bold text-blue-800">{children.length}</p>
                  </div>
                  <Users className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600">Upcoming Appointments</p>
                    <p className="text-2xl font-bold text-green-800">{appointments.length}</p>
                  </div>
                  <Calendar className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-600">Pending Invoices</p>
                    <p className="text-2xl font-bold text-amber-800">
                      {invoices.filter(i => i.status !== 'paid').length}
                    </p>
                  </div>
                  <CreditCard className="w-8 h-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-purple-600">Shared Documents</p>
                    <p className="text-2xl font-bold text-purple-800">{documents.length}</p>
                  </div>
                  <FileText className="w-8 h-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Info */}
          {selectedChild && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {selectedChild.first_name} {selectedChild.last_name}
                </CardTitle>
                <CardDescription>Quick overview</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Next Appointment */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#2a9d8f]" />
                      Next Appointment
                    </h4>
                    {appointments.length > 0 ? (
                      <div>
                        <p className="font-medium">{appointments[0].title || 'Therapy Session'}</p>
                        <p className="text-sm text-slate-600">
                          {format(parseISO(appointments[0].date), 'EEEE, MMMM d, yyyy')}
                        </p>
                        <p className="text-sm text-slate-600">
                          {appointments[0].start_time} - {appointments[0].end_time}
                        </p>
                        {appointments[0].therapist_name && (
                          <p className="text-sm text-slate-500">with {appointments[0].therapist_name}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-slate-500">No upcoming appointments</p>
                    )}
                  </div>

                  {/* Recent Documents */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[#2a9d8f]" />
                      Recent Documents
                    </h4>
                    {documents.length > 0 ? (
                      <div className="space-y-1">
                        {documents.slice(0, 3).map(doc => (
                          <p key={doc.id} className="text-sm text-slate-600 truncate">
                            {doc.original_filename}
                          </p>
                        ))}
                        {documents.length > 3 && (
                          <Button variant="link" size="sm" className="p-0 h-auto" onClick={() => setActiveTab('documents')}>
                            +{documents.length - 3} more
                          </Button>
                        )}
                      </div>
                    ) : (
                      <p className="text-slate-500">No documents shared yet</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Appointments Tab */}
        <TabsContent value="appointments">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#2a9d8f]" />
                Upcoming Appointments
                {selectedChild && <span className="text-slate-500 font-normal">for {selectedChild.first_name}</span>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {appointments.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Calendar className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No upcoming appointments</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {appointments.map(apt => (
                    <div key={apt.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#1e3a5f] text-white rounded-lg flex flex-col items-center justify-center">
                          <span className="text-xs">{format(parseISO(apt.date), 'MMM')}</span>
                          <span className="text-lg font-bold">{format(parseISO(apt.date), 'd')}</span>
                        </div>
                        <div>
                          <p className="font-medium">{apt.title || 'Therapy Session'}</p>
                          <p className="text-sm text-slate-600">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {apt.start_time} - {apt.end_time}
                          </p>
                          {apt.therapist_name && (
                            <p className="text-sm text-slate-500">with {apt.therapist_name}</p>
                          )}
                        </div>
                      </div>
                      <Badge className="bg-green-100 text-green-800">Confirmed</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#2a9d8f]" />
                Shared Documents
                {selectedChild && <span className="text-slate-500 font-normal">for {selectedChild.first_name}</span>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No documents shared yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {documents.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100">
                      <div className="flex items-center gap-3">
                        <FileText className="w-8 h-8 text-blue-500" />
                        <div>
                          <p className="font-medium text-sm">{doc.original_filename}</p>
                          <p className="text-xs text-slate-500">
                            {doc.category?.replace(/_/g, ' ')} • {format(parseISO(doc.uploaded_at), 'MMM d, yyyy')}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="icon" title="View">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Download">
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments" className="space-y-4">
          {/* Bank Details Card */}
          <Card className="bg-gradient-to-r from-[#1e3a5f] to-[#2a5a8f] text-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Building className="w-5 h-5" />
                Bank Transfer Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-blue-200">Account Name</p>
                  <p className="font-medium">{BANK_DETAILS.account_name}</p>
                </div>
                <div>
                  <p className="text-sm text-blue-200">Account Number</p>
                  <p className="font-medium font-mono">{BANK_DETAILS.account_number}</p>
                </div>
                <div>
                  <p className="text-sm text-blue-200">Sort Code</p>
                  <p className="font-medium font-mono">{BANK_DETAILS.sort_code}</p>
                </div>
                <div>
                  <p className="text-sm text-blue-200">Bank</p>
                  <p className="font-medium">{BANK_DETAILS.bank_name}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Invoices */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[#2a9d8f]" />
                Invoices
              </CardTitle>
            </CardHeader>
            <CardContent>
              {invoices.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CreditCard className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No invoices found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {invoices.map(inv => (
                    <div key={inv.id} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-medium">{inv.invoice_number}</p>
                          <p className="text-sm text-slate-600">
                            {inv.child_name} • {format(parseISO(inv.created_at), 'MMM d, yyyy')}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-[#1e3a5f]">£{inv.total?.toFixed(2)}</p>
                          {getStatusBadge(inv.status)}
                        </div>
                      </div>
                      
                      {inv.status !== 'paid' && (
                        <div className="flex gap-2 mt-3 pt-3 border-t">
                          <Button 
                            size="sm" 
                            className="bg-[#2a9d8f] hover:bg-[#238b7e]"
                            onClick={() => initiateStripePayment(inv.id)}
                          >
                            <CreditCard className="w-4 h-4 mr-2" />
                            Pay with Card
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => initiateBankTransfer(inv.id)}
                          >
                            <Building className="w-4 h-4 mr-2" />
                            Bank Transfer
                          </Button>
                        </div>
                      )}

                      {inv.bank_transfer_reference && inv.status !== 'paid' && (
                        <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                          <p className="text-blue-800">
                            Transfer Reference: <strong>{inv.bank_transfer_reference}</strong>
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Receipts */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-[#2a9d8f]" />
                Payment Receipts
              </CardTitle>
            </CardHeader>
            <CardContent>
              {receipts.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Receipt className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No receipts yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {receipts.map(receipt => (
                    <div key={receipt.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-8 h-8 text-green-500" />
                        <div>
                          <p className="font-medium">{receipt.receipt_number}</p>
                          <p className="text-sm text-slate-600">
                            £{receipt.amount?.toFixed(2)} • {receipt.payment_method?.replace(/_/g, ' ')}
                          </p>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => downloadReceipt(receipt.id)}>
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#2a9d8f]" />
                Payment History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {paymentHistory.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Clock className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No payment history</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {paymentHistory.map(txn => (
                    <div key={txn.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium">{txn.invoice_number}</p>
                        <p className="text-sm text-slate-600">
                          {txn.child_name} • {txn.payment_method?.replace(/_/g, ' ')}
                        </p>
                        <p className="text-xs text-slate-500">
                          {format(parseISO(txn.created_at), 'MMM d, yyyy HH:mm')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">£{txn.amount?.toFixed(2)}</p>
                        {getStatusBadge(txn.payment_status)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Events Tab */}
        <TabsContent value="events">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PartyPopper className="w-5 h-5 text-[#2a9d8f]" />
                Upcoming Events
              </CardTitle>
            </CardHeader>
            <CardContent>
              {events.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <PartyPopper className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No upcoming events</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {events.map(event => (
                    <div key={event.id} className="p-4 border rounded-lg hover:bg-slate-50">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 bg-[#2a9d8f] text-white rounded-lg flex flex-col items-center justify-center">
                          <span className="text-xs">{format(parseISO(event.date), 'MMM')}</span>
                          <span className="text-xl font-bold">{format(parseISO(event.date), 'd')}</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-[#1e3a5f]">{event.title}</h4>
                          <p className="text-sm text-slate-600 mt-1">{event.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                            {event.start_time && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {event.start_time} - {event.end_time}
                              </span>
                            )}
                            {event.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {event.location}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
